import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import { fetchQuery } from "relay-runtime"
import { isTimeToTreatPointOfCare } from "@/modules/patients/detail/progress-note/point-of-care/constants"
import type {
  chTimeToTreatQuery,
  chTimeToTreatQuery$data,
} from "@/modules/patients/detail/progress-note/point-of-care/services/__generated__/chTimeToTreatQuery.graphql"
import type {
  deTimeToTreatQuery,
  deTimeToTreatQuery$data,
} from "@/modules/patients/detail/progress-note/point-of-care/services/__generated__/deTimeToTreatQuery.graphql"
import { ch_TimeToTreatQuery } from "@/modules/patients/detail/progress-note/point-of-care/services/chTimeToTreatQuery"
import { de_TimeToTreatQuery } from "@/modules/patients/detail/progress-note/point-of-care/services/deTimeToTreatQuery"
import type {
  PointOfCareOption,
  PointOfCareState,
  TimeToTreatByPointOfCare,
  TimeToTreatOption,
  TimeToTreatPointOfCare,
} from "@/modules/patients/detail/progress-note/point-of-care/types"
import { RelayEnvironment } from "@/relay-environment"
import { type Country, PointOfCareTypeValues } from "@/types/__generated__/union-types"

type TimeToTreatResponse = chTimeToTreatQuery$data | deTimeToTreatQuery$data | undefined

const emptyTimeToTreatByPointOfCare = (): Record<TimeToTreatPointOfCare, TimeToTreatOption[]> => ({
  GENERAL_PRACTITIONER: [],
  SPECIALIST: [],
  HOSPITAL_ELECTIVE: [],
  HOSPITAL_EMERGENCY_ROOM: [],
  MEDICAL_EMERGENCY_OR_PERMANENCE: [],
})

const groupTimeToTreatByPointOfCare = (
  timeToTreatResponse: TimeToTreatResponse,
  t: TFunction,
): TimeToTreatByPointOfCare => {
  const grouped = emptyTimeToTreatByPointOfCare()
  for (const { pointOfCare, timesToTreat } of timeToTreatResponse?.timesToTreatByPointOfCare ?? []) {
    if (!isTimeToTreatPointOfCare(pointOfCare)) continue

    grouped[pointOfCare] = timesToTreat.map((timeToTreatType) => ({
      value: timeToTreatType,
      label: t(`episodeOfCare.progressNote.pointOfCare.timeToTreatOptionList.${timeToTreatType.toLowerCase()}`),
    }))
  }
  return grouped
}

const toPointOfCareItems = (hasExternalAssistance: boolean, t: TFunction): PointOfCareOption[] =>
  PointOfCareTypeValues.filter(
    (pointOfCareType) => pointOfCareType !== "EXTERNAL_ASSISTANCE" || hasExternalAssistance,
  ).map((pointOfCareType) => ({
    label: t(`episodeOfCare.progressNote.pointOfCare.types.${pointOfCareType}`),
    value: pointOfCareType,
  }))

export const useFetchPointOfCareData = (country: Country, hasExternalAssistance: boolean) => {
  const { t } = useTranslation()

  const [timeToTreatResponse, setTimeToTreatResponse] = useState<TimeToTreatResponse>(undefined)
  const isLoadingRef = useRef(false)

  const output = useMemo(
    (): PointOfCareState => ({
      pointOfCareItems: toPointOfCareItems(hasExternalAssistance, t),
      timeToTreatByPointOfCare: groupTimeToTreatByPointOfCare(timeToTreatResponse, t),
    }),
    [timeToTreatResponse, hasExternalAssistance, t],
  )

  useEffect(() => {
    if (isLoadingRef.current) return

    isLoadingRef.current = true
    const fetchTimeToTreat = async () => {
      try {
        const response = await fetchQuery<chTimeToTreatQuery | deTimeToTreatQuery>(
          RelayEnvironment,
          country === "CH" ? ch_TimeToTreatQuery : de_TimeToTreatQuery,
          {},
          { fetchPolicy: "store-or-network" },
        ).toPromise()
        setTimeToTreatResponse(response)
      } finally {
        isLoadingRef.current = false
      }
    }
    void fetchTimeToTreat()
  }, [country])

  return output
}
